




















import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import TrialCancellationContainer from 'frontend/views/premium/cancellation/components/TrialCancellationContainer.vue';
import TrialCancellationActionsUi from 'frontend/views/premium/cancellation/components/TrialCancellationActionsUi.vue';
import OrbitCalendarSvg from 'frontend/views/premium/cancellation/svgs/OrbitCalendarSvg.vue';
import {
  CancellationFlowDirection,
  CancellationFlowEventfulRouteIndex,
} from 'frontend/views/premium/cancellation/types';
import {
  cancellationApi,
  defaultAbortEventfulRoute,
  defaultNextEventfulRoute,
} from 'frontend/views/premium/cancellation/utils';
import { mapState } from 'vuex';
import { User } from 'frontend/types';
import { postData } from 'frontend/utils/ajax';

type ValidOption = 'group_subscription' | 'monthly_subscription' | 'lifetime_plan';

type PlanStatusIdentifier = 'different_plan_status';

interface PlanStatusContent {
  title: string;
  subtitle: string;
  friendly: string;
  abortBtnText?: string | undefined;
}

@Component({
  components: {
    TrialCancellationContainer,
    TrialCancellationActionsUi,
    OrbitCalendarSvg,
  },
  computed: {
    ...mapState('subscriptionEndDate', ['endDate']),
    ...mapState('globals', ['user']),
  },
})
export default class TrialCancellationPlanStatus extends Vue {
  private loading = false;

  private endDate!: string;

  private user!: User;

  private eventfulRouteIndex: CancellationFlowEventfulRouteIndex<ValidOption> = {
    group_subscription: {
      next: defaultNextEventfulRoute(),
      abort: defaultAbortEventfulRoute(),
    },
    monthly_subscription: {
      next: defaultNextEventfulRoute(),
      abort: defaultAbortEventfulRoute(),
    },
    lifetime_plan: {
      next: defaultNextEventfulRoute(),
      abort: defaultAbortEventfulRoute(),
    },
  };

  private planOption: ValidOption | null = null;

  private async emailSupport(): Promise<void> {
    const apiEndpoint = this.$router.resolve({
      name: 'payment_trial_cancel_diff_plan_send_support_reminder',
    }).href;
    await postData(apiEndpoint, {
      user_id: this.user.user_id,
      user_email: this.userEmail,
      trial_end_date: this.endDate,
      plan_token: this.optionContent.friendly,
    });
  }

  private async actionHandler(direction: CancellationFlowDirection): Promise<void> {
    this.loading = true;
    const { handleTrackedRedirect } = cancellationApi(this.eventfulRouteIndex);
    await handleTrackedRedirect(direction, this.planOption, this.$router);
  }

  private async created() {
    const option: string | undefined = this.$route.params?.option;
    if (this.isValidOption(option)) {
      this.planOption = option;
      // email support to remind this user to switch to this plan after trial ends
      this.emailSupport();
    } else {
      await this.$router.push({
        name: 'payment_trial_cancel_different_plan_survey',
      });
    }
  }

  private isValidOption(plan: string | undefined): plan is ValidOption {
    const validOptions: ValidOption[] = [
      'monthly_subscription',
      'lifetime_plan',
      'group_subscription',
    ];
    return validOptions.includes(plan as ValidOption);
  }

  private get optionContent(): PlanStatusContent {
    switch (this.planOption) {
      case 'monthly_subscription':
        return {
          title: 'Switch to monthly after your trial is over',
          subtitle: `We're currently working on making it easier to switch plans. We’ll email
                    <strong>${this.userEmail}</strong> when your free trial period is up on
                    <strong>${this.endDate}</strong> about how to subscribe to a monthly plan.`,
          friendly: 'monthly',
        };
      case 'lifetime_plan':
        return {
          title: 'Upgrade to lifetime after your trial ends',
          subtitle:
            'You can upgrade to lifetime (at a pro-rated price!) as soon as your free trial ' +
            'period is over. Questions about how this works? Email ' +
            '<a class="gray" href="mailto:support@brilliant.org">support@brilliant.org</a>',
          friendly: 'lifetime',
          abortBtnText: 'Keep my free trial & upgrade to lifetime after',
        };
      case 'group_subscription':
        return {
          title: 'Switch to a group plan when your trial is up',
          subtitle: `We're currently working on making it easier to switch plans. We’ll email
                    <strong>${this.userEmail}</strong> when your free trial period is up on
                    <strong>${this.endDate}</strong> about how to subscribe to a group plan.`,
          friendly: 'group',
        };
      default:
        return { title: '', subtitle: '', friendly: '' };
    }
  }

  private get userEmail(): string {
    return this.user.email;
  }
}
